import React, { useCallback, useEffect, useRef } from 'react';
import { Global } from '@emotion/react';

import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { XIcon } from '@zola/zola-ui/src/components/SvgIconsV3/X';
import useClickOutside from '@zola/zola-ui/src/hooks/useClickOutside';
import useSaveChangesPrompt from '@zola/zola-ui/src/hooks/useSaveChangesPrompt';
import useSimpleScrollLock from 'lib/hooks/useSimpleScrollLock';

import { DrawerProvider, useDrawerControls, DrawerContextStateType } from './context';

import {
  Overlay,
  Container,
  HeaderBar,
  CloseButton,
  ScreenReaderText,
  ScrollContainer,
  UnsavedChangesPrompt,
  PromptOverlay,
  PromptTitle,
  ButtonContainer,
  Discard,
  lockBgscrolling,
} from './Drawer.styles';

export type DrawerProps = {
  className?: string;
  /** Locks background scrolling in place.  We would like this to default to true, but the scroll lock jumps the background to the top top of the page, so there is a tradeoff to be made. */
  lockBgScrolling?: boolean;

  /** Should the drawer close when the 'escape' key is press.  Default true: close the drawer on escape. */
  closeOnEscape?: boolean;
};

const Drawer: React.FC<DrawerProps> = ({ children, lockBgScrolling, closeOnEscape = true }) => {
  const ref = useRef<HTMLDivElement>(null);
  const {
    isOpen = false,
    showSavePrompt = false,
    onClose,
    onSave,
    onBack,
    unsavedChange,
    setIsOpen,
    setShowSavePrompt,
    setOnUnsavedChanges,
  } = useDrawerControls();
  const { key: unsavedChangeKey, onUnsavedChanges } = unsavedChange || {};
  /** This will catch a route change if it can't be caught internally, i.e. a full-page refresh route change vs shallow change of react-router; */
  useSaveChangesPrompt(!!onUnsavedChanges);

  useSimpleScrollLock(!!lockBgScrolling && isOpen);

  // close function for X icon or clicking the opacity overlay
  const handleCloseDrawer = useCallback(() => {
    if (onUnsavedChanges) onUnsavedChanges();
    else {
      if (onClose) onClose();
      setIsOpen(false);
    }
  }, [onClose, setIsOpen, onUnsavedChanges]);

  useClickOutside({
    ref,
    onClickOutside: handleCloseDrawer,
    isOpen,
  });

  useEffect(() => {
    // Close drawer when hitting "Esc".
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        event.stopPropagation();
        handleCloseDrawer();
      }
    };
    window.addEventListener('keydown', close, true);

    return function removeEventListenerAndReturnFocus() {
      window.removeEventListener('keydown', close, true);
    };
  }, [closeOnEscape, handleCloseDrawer]);

  // Clicking 'Discard' in Save Change prompt
  const handleCloseFromPrompt = () => {
    setShowSavePrompt(false);
    const nextUnsavedChange = setOnUnsavedChanges({ key: unsavedChangeKey as string });
    setTimeout(() => {
      // Check if there are other unsaved changes on a different layer
      if (nextUnsavedChange?.onUnsavedChanges) {
        nextUnsavedChange?.onUnsavedChanges();
      }
      // else prioritize onBack, then onClose, then simply close drawer if nothing else defined
      else if (onBack) onBack();
      else if (onClose) onClose();
      else setIsOpen(false);
    }, 0);
  };

  // Clicking 'Keep editing'
  const handleContinueEditing = () => {
    setShowSavePrompt(false);
  };

  // Clicking 'Save'
  const handleSaveAndBackOrClose = () => {
    if (onSave) onSave();
    // else fire onBack if defined, takes priority over onClose
    else if (onBack) onBack();
    // else fire onClose
    else if (onClose) onClose();
  };

  return (
    <Overlay data-testid="zui-Drawer" isOpen={isOpen}>
      <Container isOpen={isOpen} ref={ref}>
        <ScrollContainer id="drawer-scroll-container">
          <HeaderBar>
            <CloseButton onClick={handleCloseDrawer}>
              <XIcon height={20} width={20} showTitle={false} />
              <ScreenReaderText>Close</ScreenReaderText>
            </CloseButton>
          </HeaderBar>
          {children}
          {showSavePrompt && (
            <PromptOverlay>
              <UnsavedChangesPrompt data-testid="zui-Drawer__prompt">
                <PromptTitle>Are you sure? You have unsaved changes.</PromptTitle>
                <ButtonContainer>
                  <ButtonV3 onClick={handleContinueEditing} variant="secondary" size="small">
                    Keep editing
                  </ButtonV3>
                  <ButtonV3 onClick={handleSaveAndBackOrClose} size="small">
                    Save
                  </ButtonV3>
                </ButtonContainer>
                <Discard role="button" onClick={handleCloseFromPrompt}>
                  Discard
                </Discard>
              </UnsavedChangesPrompt>
            </PromptOverlay>
          )}
        </ScrollContainer>
      </Container>
    </Overlay>
  );
};

const DrawerWithProvider: React.FC<DrawerProps & DrawerContextStateType> = ({
  children,
  className,
  lockBgScrolling,
  closeOnEscape,
  ...contextProps
}) => (
  <DrawerProvider {...contextProps}>
    <Global styles={lockBgscrolling} />
    <Drawer className={className} lockBgScrolling={lockBgScrolling} closeOnEscape={closeOnEscape}>
      {children}
    </Drawer>
  </DrawerProvider>
);

export function withDrawer<P>(Component: React.ComponentType<P>, opts?: DrawerProps) {
  const ComponentWrappedInProvider: React.FC<P> = props => {
    return (
      <DrawerWithProvider {...opts}>
        <Component {...props} />
      </DrawerWithProvider>
    );
  };

  return ComponentWrappedInProvider;
}

export default DrawerWithProvider;
